import { HTMLAttributes, ReactElement, Ref, useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { classNames } from '@m1work/shared-utils';

import { getResetCss } from './getResetCss';
import styles from './SandboxedHtml.module.scss';

import { localizeStorageUrls, sanitizeHtml } from '@/shared/utils';

// Inside the shadow root the module's classes don't apply, so the rules for the banner's own markup live here.
// The reset's `html, :host` rule gives the host the page background; the host must stay transparent like before.
const SHADOW_BASE_CSS = `
:host {
  background-color: transparent;
}

:host > * {
  min-width: 0;
  min-height: 0;
  margin: 0 !important;
  aspect-ratio: auto !important;
}

* {
  max-width: 100% !important;
  box-sizing: border-box;
}

img,
video,
svg,
table,
iframe {
  max-height: 100%;
  object-fit: contain;
}
`;

/**
 * How the banner's images load: `priority` for the page's main image (LCP), `eager` for the first screen,
 * `background` for hidden carousel slides (they must be ready when they slide in), `lazy` for the rest.
 */
export type BannerImageLoading = 'priority' | 'eager' | 'background' | 'lazy';

// A banner that sets these itself keeps them.
const LOADING_ATTRIBUTES = ['loading', 'fetchpriority'];

function setImageLoading(image: HTMLImageElement, loading: BannerImageLoading): void {
    if (LOADING_ATTRIBUTES.some((name) => image.hasAttribute(name))) {
        return;
    }

    if (loading === 'lazy') {
        image.setAttribute('loading', 'lazy');
    } else if (loading === 'priority') {
        image.setAttribute('fetchpriority', 'high');
    } else if (loading === 'background') {
        image.setAttribute('fetchpriority', 'low');
    }
}

export interface SandboxedHtmlProps extends Omit<HTMLAttributes<HTMLDivElement>, 'dangerouslySetInnerHTML'> {
    /** Raw HTML from the CMS; it is sanitised here (`sanitizeHtml`). */
    html: string;
    /** How the banner's images load. @default 'lazy' */
    imageLoading?: BannerImageLoading;
    className?: string;
    /** The host element; the banner markup is in its `shadowRoot`. */
    ref?: Ref<HTMLDivElement>;
}

/**
 * Renders promo banner HTML from the CMS after sanitising it, inside a shadow root: the banner's `<style>`
 * (even `*` or `body` selectors) can't restyle the page. The content appears after hydration, not in the SSR HTML.
 * Holds the app's sanitising rules, so it stays in web.
 *
 * @example
 * <SandboxedHtml html={banner.html} />
 */
export function SandboxedHtml({
    html,
    imageLoading = 'lazy',
    className,
    ref,
    ...props
}: SandboxedHtmlProps): ReactElement {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const sanitizedHtml = useMemo(() => localizeStorageUrls(sanitizeHtml(html)), [html]);

    const setHostRef = useCallback(
        (node: HTMLDivElement | null): void => {
            hostRef.current = node;

            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                ref.current = node;
            }
        },
        [ref],
    );

    // A layout effect: the markup is in place before paint and before the parent's effects read it.
    useLayoutEffect(() => {
        const host = hostRef.current;

        if (!host) {
            return;
        }

        const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
        // Images in a template don't load yet, so their loading attributes are in place before the first request.
        const template = document.createElement('template');

        template.innerHTML = `<style>${getResetCss()}${SHADOW_BASE_CSS}</style>${sanitizedHtml}`;
        template.content.querySelectorAll('img').forEach((image) => setImageLoading(image, imageLoading));
        root.replaceChildren(template.content);
    }, [sanitizedHtml, imageLoading]);

    return <div ref={setHostRef} className={classNames(styles.sandbox, className)} {...props} />;
}
